import React from "react"
import { useTranslation } from "react-i18next"

import { Spinner } from "components/Elements"

import { User, UserData, ErrorResponse } from "models"
import { useAlert, useOperator, useUser } from "hooks"

import PrivateLayout from "components/Layout/PrivateLayout"
import * as UserApi from "../api/UserApi"
import {
	UserPassword,
	UserCredentials,
	UserInfo,
	UserAddress,
	UserDeleteAccount,
} from "../components"
import { pageStatusEnum } from "utils"
import { Seo } from "components/Seo"

// prettier-ignore
export type PageAction = { type: "LOADING" } | { type: "SUCCESS"; userData: Partial<User & UserData> } | { type: "ERROR" }
export type PageDispatch = (action: PageAction) => void

const pageReducer = (state: any, action: PageAction) => {
	switch (action.type) {
		case "LOADING": {
			return {
				status: pageStatusEnum.loading,
				userData: null,
			}
		}
		case "SUCCESS": {
			return {
				status: pageStatusEnum.complete,
				userData: action.userData,
			}
		}
		case "ERROR": {
			return {
				status: pageStatusEnum.errored,
				userData: null,
			}
		}
	}
}

export const AccountPage: React.FC = () => {
	const [{ status, userData }, dispatchPage] = React.useReducer(pageReducer, {
		status: pageStatusEnum.loading,
		userData: null,
	})

	const { t } = useTranslation()
	const { alert } = useAlert()
	const { currentOperator } = useOperator()
	const { user, dispatch: dispatchUser } = useUser()

	const isLoading = status === pageStatusEnum.loading
	const isComplete = status === pageStatusEnum.complete
	const isErrored = status === pageStatusEnum.errored

	const fetchUser = async (forceUpdate = false) => {
		if (user && !forceUpdate) {
			// return the user saved in the context
			// this prevent the route to be called on each visit
			dispatchPage({ type: "SUCCESS", userData: user })
		} else {
			try {
				dispatchPage({ type: "LOADING" })
				const { res: fetchedUser, getError } = await UserApi.getUser()
				if (getError) throw getError()

				if (fetchedUser) {
					dispatchUser({ type: "USER", user: fetchedUser })
					dispatchPage({ type: "SUCCESS", userData: fetchedUser })
				} else {
					throw getError()
				}
			} catch (error) {
				console.error(error)
				dispatchPage({ type: "ERROR" })
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			}
		}
	}

	React.useEffect(() => {
		fetchUser()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<PrivateLayout>
			{currentOperator && (
				<Seo
					title={t("user", {
						ns: "seo",
						operator: currentOperator.name,
					})}
				/>
			)}
			<div className="mx-auto p-2 lg:p-10">
				<div className="max-w-screen-3xl mx-auto px-4 py-8 lg:py-16">
					<div className="flex items-center mb-2">
						<h1 className="c-h1 leading-tight mb-2 md:mb-8">
							{t("account.title", { ns: "auth" })}
						</h1>
						<p className="ml-4 text-primary opacity-40">
							{t("accountID", { ns: "user" })} : {user.account_id}
						</p>
					</div>

					{isLoading && (
						<div className="flex justify-center my-8">
							<Spinner color={"var(--primaryColor)"} size={"100px"} />
						</div>
					)}

					{isErrored && (
						<div className="flex justify-center my-8">
							{t("account.error", { ns: "auth" })}
						</div>
					)}

					{isComplete && (
						<div className="flex justify-between flex-wrap">
							<div className="w-full lg:w-1/2 lg:pr-8 pb-8 md:pb-14">
								<div className="mb-16">
									<UserInfo
										userData={userData}
										refreshUser={() => fetchUser(true)}
									/>
								</div>
								<UserAddress
									userData={userData}
									refreshUser={() => fetchUser(true)}
								/>
							</div>

							<div className="w-full lg:w-1/2 lg:pl-8">
								<UserCredentials
									userData={userData}
									refreshUser={() => fetchUser(true)}
								/>

								<UserPassword
									userData={userData}
									refreshUser={() => fetchUser(true)}
								/>

								<UserDeleteAccount userData={userData} />
							</div>
						</div>
					)}
				</div>
			</div>
		</PrivateLayout>
	)
}
